/**
 * Tool executor — lives in the frontend app, NOT in @edge/edge-ai.
 *
 * Edge AI requests actions via events. The frontend validates inputs,
 * takes snapshots, and executes against the workspace store.
 * This is the security boundary — the Edge AI package cannot mutate state directly.
 */

import type { Pending, ToolCall, ToolResult, TurnAccess, WalkthroughOffer } from '@edge/edge-ai'
import type { ActionCallbacks, ActionSnapshot } from './types'
import { recordingWrites, takeSnapshot, revertSnapshot } from './action-snapshot'
import { COMPONENT_META } from '@/workspace/components/add-panel-menu-constants'
import { getTemplate } from './strategy-templates'
import {
  ALLOWANCE_SPENT_MESSAGE,
  currentClientTurnId,
  currentTurnModality,
  currentTurnToken,
  getToolDefinition,
  READ_ONLY_TOOLS,
} from '@edge/edge-ai'
import { getPanelLiveState } from '@/stores/panel-state-registry'
import { usePanelSelectionStore } from '@/stores/panel-selection-store'
import { usePanelActivityStore } from '@/stores/panel-activity-store'
import type { AIFilterInput } from './filter-builder'
import {
  resolveColumnTarget,
  resolveFilterTarget,
  type CapabilityResult,
  type FilterTarget,
  type PanelColumns,
} from './panel-capabilities'
import { getColumnCatalogText } from './column-catalogs'
import { drainFramesFromBuffer, parseSseFrame } from '@/lib/sse-parse'
import { describeNodeTypes } from '@/features/backtest/graph-engine/catalog'
import type { Edge, Node } from '@xyflow/react'
import { computeGraphReadiness } from '@/features/backtest/hooks/use-graph-readiness'
import { aiGraphView } from '@/features/backtest/utils/ai-graph-view'
import type { NodeData } from '@/features/backtest/types/nodes'
import { graphToRecipe } from '@/features/backtest/graph-engine/graph-to-recipe'
import { applyGraphOps } from '@/features/backtest/graph-engine/graph-ops'
import type { GraphOp } from '@/features/backtest/graph-engine/graph-ops'
import type { EngineError } from '@/features/backtest/graph-engine/types'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import { buildEditDiff, affectedNodeIds, type DiffLine } from './edit-diff'
import { recordGraphEdit, recordAsyncGraphEdit } from './record-graph-edit'
import {
  GRAPH_EDIT_METHODS,
  GRAPH_TOOL_NAMES,
  SAVED_STRATEGY_TOOL_NAMES,
  assertGraphToolAccess,
  executeGraphMethod,
  serializeGraphResult,
} from './graph-tools'
import {
  getActiveGraphApi,
  assertActiveGraphWritable,
} from '@/features/backtest/graph-api/active-context'
import { useActionRegistrationStore } from './action-registration-store'
import { ASSISTANT_PERSONA } from './companion-personas'
import { recordSavedStrategyEdit } from './saved-strategy-edits'
import {
  withGraphAction,
  withGraphApiInvocation,
  type AutomationTurn,
} from '@/features/backtest/hooks/graph-automation'
import { executeAnalyzeBacktest } from './backtest-analysis-tool'
import { resolveRoute, ROUTE_CATALOG } from './route-catalog'
import {
  GEAR_SELECTOR,
  panelWindowFromBody,
  startWalkthrough,
  type WalkthroughStep,
} from './companion-walkthrough-store'
import { useLearnProgressStore } from '@/features/learn/learn-progress-store'
import { markWalkthroughDone } from '@/features/onboarding/checklist-signals'
import { companionClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import { debugToolsEnabled } from '@/ett-debug-tools'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import { FLAGS } from '@/services/analytics/feature-flags'
import { isFeatureFlagOn } from '@/services/analytics/use-feature-flag'
import { emitAiDebugRound } from '@/ett-debug-tools/ai/emit-ai-debug'
import type { LlmCallMeta, LlmSessionTotals } from '@/ett-debug-tools/ai/llm-call-meta'
import {
  useLanguageStore,
  languageName,
  forcedLanguageName,
  type LanguageCode,
} from '@/stores/language-store'
import { useTierStore } from '@/stores/tier-store'
import { useThreadStore } from '@/stores/thread-store'
import { useAccountStore } from '@/stores/account-store'
import {
  createSavedStrategyFromRecipe,
  readSavedStrategyGraph,
  applySavedGraphOps,
} from '@/features/backtest/headless-graph-service'
import { runHeadlessSingleRun } from '@/features/backtest/headless-run'
import type { Recipe } from '@/features/backtest/utils/recipe-types'
import { assertAutomationTurnCurrent } from '@/features/backtest/hooks/graph-automation'
import { setHeadlessExecutionContext } from './headless-execution-context'
import { getResolvedPlanTier } from '@/stores/plan-tier-store'
import { clampTierToPlan } from '@/features/companion/answer-tiers'
import { VOICE_ANALYST_TIER } from './voice-style'
import { currentTurnPanels } from './turn-panels'
import { useDocGapStore } from './doc-gap-store'
import { onScreenWatchlists } from './screen-watchlists'
import { NO_RECIPE_MATCH } from './knowledge-messages'

/**
 * Minimum retrieval score for an entry's authored steps to auto-launch a guided
 * walkthrough. Below this the match is too weak to hijack the answer into a
 * pointing flow, so we fall back to prose.
 */
const WALKTHROUGH_MIN_SCORE = 0.35

/**
 * Jev's verdict (knowledge-search.ts `walkthrough_fit`, the probability that
 * doing the walkthrough accomplishes what was asked) needed to offer or start
 * it. Live 2026-10-18: the right tours scored 0.77 to 0.91, wrong ones 0.02 to 0.27.
 */
const WALKTHROUGH_MIN_FIT = 0.5

/**
 * Unvetted (Jev down, or a server without the vet), a walkthrough must score
 * within this much of the best match. Calibrated on the same probes: kept
 * tours sat 0.07 below, wrong ones 0.15 to 0.22 below.
 */
const WALKTHROUGH_MAX_GAP = 0.1

/**
 * Prepended to retrieved how-to content. Turns the tool result into a hard
 * grounding contract: the model may only state what the docs below actually
 * contain, and must fact-check its answer against them — no invented controls.
 */
const GROUNDING_PREAMBLE =
  'These are the ONLY verified facts about how this works in the app — treat them as the record of what exists. Answer strictly from them, in your own words. Do NOT mention any tab, button, menu, or step that is not written below (no invented "palettes", pickers, or dialogs). If the facts below do not actually answer the question, say you don\'t see that in the app rather than guessing.'

/**
 * Returned when retrieval can't confirm the steps (KB unavailable, lookup
 * errored). With no grounding, the model must NOT invent a UI path — it hedges.
 */
const UNVERIFIED_GUIDANCE =
  "I can't verify the exact steps against the app's documentation right now. Tell the user you're not certain of the current steps and suggest they open the panel's ⚙ settings to look — do NOT describe specific tabs, buttons, or steps you can't verify."

/** Friendly display names for the panel push-back messages. */
const PANEL_LABELS: Record<string, string> = {
  scanner: 'Scanner',
  'multi-scanner': 'Multi-Scanner',
  history: 'History',
  'history-records': 'History',
  watchlist: 'Watchlist',
  news: 'News',
  calendar: 'Calendar',
  'ticker-stats': 'Ticker Stats',
  'key-stats': 'Key Stats',
}

/** Normalize the model's free-form `panel` arg toward a `data-panel-type`. */
function normalizePanel(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/\b(the|my|a|an)\b/g, ' ') // drop articles
    .replace(/\bpanel\b/g, ' ') // "scanner panel" → "scanner"
    .trim()
    .replace(/\s+/g, '-')
  const alias: Record<string, string> = {
    scan: 'scanner',
    scans: 'scanner',
    'multi-scan': 'multi-scanner',
    // Key Stats and Ticker Stats are separate panel types (component-registry).
    keystats: 'key-stats',
    stats: 'ticker-stats',
  }
  return alias[s] ?? s
}

function panelLabel(typeOrArg: string): string {
  return (
    PANEL_LABELS[typeOrArg] ?? typeOrArg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

/** Does a panel's `data-panel-type` match the requested (normalized) type? */
function panelTypeMatches(type: string, norm: string): boolean {
  return type === norm || type.startsWith(`${norm}-`) || norm.startsWith(`${type}-`)
}

/** The reply the user sees when the flow exists but its panel is missing or ambiguous. */
function pendingWalkthroughMessage(
  label: string,
  reason: 'missing' | 'ambiguous' | 'none-open',
): string {
  switch (reason) {
    case 'missing':
      return `I don't see a ${label} here right now. Add a ${label} panel to this layout, then press Start walkthrough.`
    case 'ambiguous':
      return `You have more than one ${label} open. Click the one you want to change, then press Start walkthrough.`
    case 'none-open':
      return `I don't see a panel with these settings here right now. Add the panel you want to change, then press Start walkthrough.`
  }
}

/**
 * Anchor a guided walkthrough to a concrete open panel, or explain why it can't.
 *
 * App-wide labels (the gear) exist on every panel, so a walkthrough MUST know
 * which panel instance to point at. We honor the model's `panel` intent and
 * validate it against the panels actually on screen — and, per the product
 * principle, PUSH BACK (return a spoken message, no walkthrough) when the panel
 * is absent or ambiguous rather than pointing at an arbitrary one.
 *
 * Candidates are restricted to panels that actually carry the settings gear, so
 * a chart/other panel of a matching name can't be mistaken for a settings-sheet
 * panel.
 */
export type PanelResolution =
  | { panelId: string; label: string }
  | { error: string; label: string; reason: 'missing' | 'ambiguous' | 'none-open' }

export function resolveWalkthroughPanel(panelArg?: string): PanelResolution {
  // A panel body carries data-panel-id + data-panel-type; its settings gear
  // lives in the react-mosaic window TOOLBAR (a sibling, not a descendant), so
  // we anchor via panelWindowFromBody (walks up to the shared window). A panel
  // is "settings-capable" only if that window holds exactly ONE gear — a
  // gear-less panel (e.g. a chart) would otherwise overshoot to a shared
  // ancestor containing several gears.
  const gearPanels = Array.from(
    document.querySelectorAll<HTMLElement>('[data-panel-id][data-panel-type]'),
  )
    .map((body) => ({ body, win: panelWindowFromBody(body) }))
    .filter((p) => p.win && p.win.querySelectorAll(GEAR_SELECTOR).length === 1)

  const idOf = (body: HTMLElement) => body.getAttribute('data-panel-id') ?? ''

  if (panelArg && panelArg.trim()) {
    const norm = normalizePanel(panelArg)
    const label = panelLabel(norm)
    const matches = gearPanels.filter((p) =>
      panelTypeMatches(p.body.getAttribute('data-panel-type') ?? '', norm),
    )
    if (matches.length === 0) {
      return { error: pendingWalkthroughMessage(label, 'missing'), label, reason: 'missing' }
    }
    if (matches.length > 1) {
      return { error: pendingWalkthroughMessage(label, 'ambiguous'), label, reason: 'ambiguous' }
    }
    return { panelId: idOf(matches[0].body), label }
  }

  // No panel specified — only safe if exactly one settings-capable panel is open.
  if (gearPanels.length === 1) {
    const type = gearPanels[0].body.getAttribute('data-panel-type') ?? ''
    return { panelId: idOf(gearPanels[0].body), label: panelLabel(type) }
  }
  if (gearPanels.length === 0) {
    return {
      error: pendingWalkthroughMessage('', 'none-open'),
      label: 'panel',
      reason: 'none-open',
    }
  }
  return {
    error: `Which panel do you want to change? You have a few open. Tell me which one (the Scanner, say), or click it and press Start walkthrough.`,
    label: 'panel',
    reason: 'ambiguous',
  }
}

/**
 * Finishing a chat-launched tour marks the Learn lesson and onboarding item
 * for that slug, exactly like a Learn-page launch. Dismissing does not.
 */
export function completionFor(slug: string | undefined): (() => void) | null {
  if (!slug) return null
  return () => {
    useLearnProgressStore.getState().completeLesson(slug)
    markWalkthroughDone(slug)
  }
}

/** Terse, model-facing error line — the LLM repair-loop payload. */
function formatEngineError(e: EngineError): string {
  const where = e.stepId ? ` @ ${e.stepId}${e.handle ? `.${e.handle}` : ''}` : ''
  return `${e.code}${where}: ${e.message}${e.suggestion ? ` (${e.suggestion})` : ''}`
}

/** Summary-level diff line for whole-graph builds (per-op detail is apply_graph_ops). */
function buildBuildSummary(nodeCount: number, appended: boolean): DiffLine[] {
  const noun = `${nodeCount} node${nodeCount === 1 ? '' : 's'}`
  return [{ kind: 'add_node', text: `+ ${noun} ${appended ? 'appended' : 'built'}` }]
}

function ok(toolUseId: string, content: string, passthrough = false): ToolResult {
  return { tool_use_id: toolUseId, success: true, content, passthrough }
}

function fail(toolUseId: string, content: string, passthrough = false): ToolResult {
  return { tool_use_id: toolUseId, success: false, content, is_error: true, passthrough }
}

/** Commit a built graph, honoring the tool's `append` flag: merge into the
 *  current graph when `append: true` (and the page supports it), else replace.
 *  Returns true if it appended. */
function applyBuiltGraph(
  call: ToolCall,
  callbacks: ActionCallbacks,
  nodes: Array<Record<string, unknown>>,
  edges: Array<Record<string, unknown>>,
): boolean {
  if (call.input.append === true && callbacks.appendNodesAndEdges) {
    callbacks.appendNodesAndEdges(nodes, edges)
    return true
  }
  callbacks.setNodesAndEdges?.(nodes, edges)
  return false
}

// ── Individual tool executors ───────────────────────────────

function executeAddPanel(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.addPanel) {
    return fail(call.tool_use_id, 'addPanel is not available on this page')
  }
  const componentType = call.input.component_type as string
  if (!componentType) {
    return fail(call.tool_use_id, 'component_type is required')
  }
  const initialState = call.input.initial_state as Record<string, unknown> | undefined
  const panelId = callbacks.addPanel(componentType, initialState)

  // Demand-load the column catalog: if this panel type supports filters/columns,
  // ship the keys back with the result so follow-up set_filters/set_columns calls
  // don't have to guess column names or hit search_knowledge.
  const catalog = getColumnCatalogText(componentType)
  const lines = [`Panel added successfully (panel_id: ${panelId}, type: ${componentType})`]
  if (catalog) {
    lines.push(`\nUse this panel_id (${panelId}) for follow-up calls on this panel.`, '', catalog)
  }
  return ok(call.tool_use_id, lines.join('\n'))
}

/** Extract all column keys referenced in a filter tree */

/** Show activity indicator on a panel during a mutation, then clear it */
let activityClearTimer: ReturnType<typeof setTimeout> | null = null

async function withPanelActivity(
  panelId: string,
  panelType: string | undefined,
  fn: () => Promise<ToolResult>,
): Promise<ToolResult> {
  const store = usePanelActivityStore.getState()
  // Start activity if not already showing on this panel
  if (store.activePanelId !== panelId) {
    store.startActivity(panelId, panelType)
  }
  // Clear any pending clear timer (another mutation might follow)
  if (activityClearTimer) clearTimeout(activityClearTimer)
  const result = await fn()
  // Clear after a delay so the shimmer is visible after the last mutation
  activityClearTimer = setTimeout(() => {
    usePanelActivityStore.getState().clearActivity()
    activityClearTimer = null
  }, 1500)
  return result
}

// ── Shared capabilities: filters and columns (spec 4.4) ──────────────────────
// Each surface implements them through its adapter (panel-capabilities.ts); the
// tools resolve where a call lands the way a chart tool does.

/** Run an adapter call, with the shimmer on a panel target. */
function applyToFilterTarget(
  call: ToolCall,
  target: FilterTarget,
  run: () => CapabilityResult | Promise<CapabilityResult>,
): Promise<ToolResult> {
  const finish = async () => {
    const result = await run()
    return result.ok ? ok(call.tool_use_id, result.message) : fail(call.tool_use_id, result.error)
  }
  return target.kind === 'panel' ? withPanelActivity(target.id, target.type, finish) : finish()
}

function filterInputs(call: ToolCall): AIFilterInput[] | null {
  const inputs = call.input.filters as AIFilterInput[]
  return Array.isArray(inputs) && inputs.length > 0 ? inputs : null
}

/**
 * Filters by column on a panel, or on the History page's page-wide filters that
 * every history panel reacts to. Each column sent replaces its filter; the
 * others stay.
 */
async function executeSetFilters(call: ToolCall, callbacks: ActionCallbacks): Promise<ToolResult> {
  const inputs = filterInputs(call)
  if (!inputs) return fail(call.tool_use_id, 'filters (non-empty array) is required')
  const target = resolveFilterTarget(callbacks, call.input.panel_id)
  if ('error' in target) return fail(call.tool_use_id, target.error)
  return applyToFilterTarget(call, target, () => target.adapter.set(target.id, inputs, callbacks))
}

/**
 * The old name for set_filters on the History page: it always sets the
 * page-wide filters. Kept working until the companion prompt and actions.md
 * name set_filters.
 */
async function executeSetHistoryFilters(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  const inputs = filterInputs(call)
  if (!inputs) return fail(call.tool_use_id, 'filters (non-empty array) is required')
  const target = resolveFilterTarget(callbacks, undefined, { page: true })
  if ('error' in target) return fail(call.tool_use_id, target.error)
  return applyToFilterTarget(call, target, () => target.adapter.set(target.id, inputs, callbacks))
}

async function executeClearFilters(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  const target = resolveFilterTarget(callbacks, call.input.panel_id, { clear: true })
  if ('error' in target) return fail(call.tool_use_id, target.error)
  return applyToFilterTarget(call, target, () => target.adapter.clear!(target.id, callbacks))
}

/** A column call's panel, loaded, and the keys it sends. */
async function columnCall(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<{ id: string; panel: PanelColumns; columns: string[] } | ToolResult> {
  if (!callbacks.updatePanel) return fail(call.tool_use_id, 'updatePanel is not available')
  const columns = call.input.columns as string[]
  if (!columns || !Array.isArray(columns)) return fail(call.tool_use_id, 'columns is required')
  const target = resolveColumnTarget(callbacks, call.input.panel_id)
  if ('error' in target) return fail(call.tool_use_id, target.error)
  const panel = await target.adapter.load(target.id, callbacks)
  if ('error' in panel) return fail(call.tool_use_id, panel.error)
  return { id: target.id, panel, columns }
}

function validateColumnKeys(
  call: ToolCall,
  columns: string[],
  validKeys: string[] | undefined,
): ToolResult | null {
  if (validKeys) {
    const validSet = new Set(validKeys)
    const invalid = columns.filter((c) => !validSet.has(c))
    if (invalid.length > 0) {
      return fail(
        call.tool_use_id,
        `Invalid column key(s): ${invalid.join(', ')}. Use search_knowledge(domain="column") to find correct keys.`,
      )
    }
  }
  return null
}

async function executeAddColumns(call: ToolCall, callbacks: ActionCallbacks): Promise<ToolResult> {
  const resolved = await columnCall(call, callbacks)
  if ('tool_use_id' in resolved) return resolved
  const { id, panel, columns } = resolved
  const invalid = validateColumnKeys(call, columns, panel.validKeys)
  if (invalid) return invalid
  panel.write([...panel.current, ...columns.filter((c) => !panel.current.includes(c))])
  return ok(call.tool_use_id, `Columns added to panel ${id}`)
}

async function executeRemoveColumns(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  const resolved = await columnCall(call, callbacks)
  if ('tool_use_id' in resolved) return resolved
  const { id, panel, columns } = resolved
  const removing = new Set(columns)
  panel.write(panel.current.filter((c) => !removing.has(c) || panel.fixed.includes(c)))
  return ok(call.tool_use_id, `Columns removed from panel ${id}`)
}

async function executeSetColumns(call: ToolCall, callbacks: ActionCallbacks): Promise<ToolResult> {
  const resolved = await columnCall(call, callbacks)
  if ('tool_use_id' in resolved) return resolved
  const { id, panel, columns } = resolved
  const invalid = validateColumnKeys(call, columns, panel.validKeys)
  if (invalid) return invalid
  // The panel's fixed columns (the ticker) stay first.
  panel.write([...panel.fixed.filter((c) => !columns.includes(c)), ...columns])
  return ok(call.tool_use_id, `Columns set on panel ${id}`)
}

async function executeGenerateHistoryLayout(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  if (!callbacks.generateLayout) {
    return fail(call.tool_use_id, 'generateLayout is not available on this page')
  }
  const prompt = call.input.prompt as string
  if (!prompt) {
    return fail(call.tool_use_id, 'prompt is required')
  }
  try {
    await callbacks.generateLayout(prompt)
    return ok(call.tool_use_id, `History layout generated for: "${prompt}"`)
  } catch (err) {
    return fail(
      call.tool_use_id,
      `Layout generation failed: ${err instanceof Error ? err.message : String(err)}`,
    )
  }
}

async function executeAddNode(call: ToolCall, callbacks: ActionCallbacks): Promise<ToolResult> {
  if (!callbacks.addNode) return fail(call.tool_use_id, 'addNode is not available on this page')
  const nodeType = call.input.node_type as string
  if (!nodeType) return fail(call.tool_use_id, 'node_type is required')
  const position = call.input.position as { x: number; y: number } | undefined
  const parameters = call.input.parameters as Record<string, unknown> | undefined
  const nodeId = await callbacks.addNode(nodeType, position, parameters)
  return ok(call.tool_use_id, `Node added (node_id: ${nodeId}, type: ${nodeType})`)
}

async function executeConnectNodes(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  if (!callbacks.connectNodes)
    return fail(call.tool_use_id, 'connectNodes is not available on this page')
  const source = call.input.source_node_id as string
  const target = call.input.target_node_id as string
  if (!source || !target)
    return fail(call.tool_use_id, 'source_node_id and target_node_id are required')
  const edgeId = await callbacks.connectNodes(
    source,
    target,
    call.input.source_handle as string | undefined,
    call.input.target_handle as string | undefined,
  )
  return ok(call.tool_use_id, `Edge created (edge_id: ${edgeId}, ${source} → ${target})`)
}

async function executeSetNodeParameters(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  if (!callbacks.setNodeParameters)
    return fail(call.tool_use_id, 'setNodeParameters is not available on this page')
  const nodeId = call.input.node_id as string
  const parameters = call.input.parameters as Record<string, unknown>
  if (!nodeId || !parameters) return fail(call.tool_use_id, 'node_id and parameters are required')
  await callbacks.setNodeParameters(nodeId, parameters)
  return ok(call.tool_use_id, `Parameters updated on node ${nodeId}`)
}

async function executeRemoveNode(call: ToolCall, callbacks: ActionCallbacks): Promise<ToolResult> {
  if (!callbacks.removeNode)
    return fail(call.tool_use_id, 'removeNode is not available on this page')
  const nodeId = call.input.node_id as string
  if (!nodeId) return fail(call.tool_use_id, 'node_id is required')
  await callbacks.removeNode(nodeId)
  return ok(call.tool_use_id, `Node ${nodeId} removed`)
}

function executeCreateStrategyTemplate(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.setNodesAndEdges)
    return fail(call.tool_use_id, 'setNodesAndEdges is not available on this page')
  const templateName = call.input.template_name as string
  if (!templateName) return fail(call.tool_use_id, 'template_name is required')
  const template = getTemplate(templateName)
  if (!template) return fail(call.tool_use_id, `Unknown template: ${templateName}`)
  // A template builds with its own settings; parameters are applied after.
  const settingsNote =
    call.input.parameters && Object.keys(call.input.parameters as object).length > 0
      ? ' with its own settings: apply the requested parameters with apply_graph_ops (read_current_graph shows the step ids)'
      : ''
  const willAppend = call.input.append === true && !!callbacks.appendNodesAndEdges
  recordGraphEdit({
    title: willAppend ? 'Strategy appended' : 'Strategy built',
    diffLines: buildBuildSummary(template.nodes.length, willAppend),
    commit: () =>
      applyBuiltGraph(
        call,
        callbacks,
        template.nodes as unknown as Array<Record<string, unknown>>,
        template.edges as unknown as Array<Record<string, unknown>>,
      ),
  })
  const appended = willAppend
  return ok(
    call.tool_use_id,
    `Strategy template "${templateName}" ${appended ? 'appended' : 'created'} with ${template.nodes.length} nodes${settingsNote}`,
  )
}

/**
 * Ask the trader to click a panel. A kind the layout has none of can never be
 * clicked, so that fails at once with how to get one, instead of waiting out
 * the selection (2026-10-18: no scanner on the layout, a 30 s wait, then
 * "select the scanner you want me to use").
 */
async function executeRequestPanelSelection(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  const panelType = call.input.panel_type as string | undefined
  const message = call.input.message as string
  if (!message) return fail(call.tool_use_id, 'message is required')
  const onLayout = panelType ? callbacks.getPanelsByType?.(panelType) : undefined
  if (panelType && onLayout && onLayout.length === 0) {
    const meta = (COMPONENT_META as Record<string, { label: string } | undefined>)[panelType]
    const label = meta?.label ?? panelType
    return fail(
      call.tool_use_id,
      `There is no ${label} panel on this layout, so there is nothing to select. Tell the ` +
        `trader so, and that they can switch to a layout that has one, or add a ${label} panel. ` +
        'Name no ticker from it, and do not pick from anywhere else in its place.',
    )
  }

  try {
    const panelId = await new Promise<string>((resolve, reject) => {
      usePanelSelectionStore
        .getState()
        .startSelection(panelType ?? null, message, resolve, (reason) => reject(new Error(reason)))
    })
    return ok(call.tool_use_id, `User selected panel: ${panelId}`)
  } catch (err) {
    return fail(call.tool_use_id, err instanceof Error ? err.message : 'Panel selection failed')
  }
}

/**
 * Why a change tool is refused on a read-only turn. There are no modes for the
 * user since 2026-10-16, so the model must not cite one; the user gets the
 * change by asking for it.
 */
const NOT_A_CHANGE_REQUEST =
  'it changes the workspace, and this message was not read as a request for a change'

function executeGetTools(call: ToolCall, access: TurnAccess): ToolResult {
  const names = call.input.tools as string[]
  if (!names || !Array.isArray(names)) return fail(call.tool_use_id, 'tools array is required')
  const results: string[] = []
  for (const name of names) {
    if (access === 'read' && !READ_ONLY_TOOLS.has(name)) {
      results.push(`**${name}**: unavailable this turn (${NOT_A_CHANGE_REQUEST})`)
      continue
    }
    const tool = getToolDefinition(name)
    if (tool) {
      results.push(`**${name}**: ${tool.description}\nSchema: ${JSON.stringify(tool.input_schema)}`)
    } else {
      results.push(`**${name}**: unknown tool`)
    }
  }
  return ok(call.tool_use_id, results.join('\n\n'))
}

function executeDeletePanel(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.deletePanel) return fail(call.tool_use_id, 'deletePanel is not available')
  const panelId = call.input.panel_id as string
  if (!panelId) return fail(call.tool_use_id, 'panel_id is required')
  callbacks.deletePanel(panelId)
  return ok(call.tool_use_id, `Panel ${panelId} deleted`)
}

function executeSetTicker(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.setTicker) return fail(call.tool_use_id, 'setTicker is not available')
  const ticker = call.input.ticker as string
  if (!ticker) return fail(call.tool_use_id, 'ticker is required')
  const panelId = call.input.panel_id as string | undefined
  callbacks.setTicker(ticker.toUpperCase(), panelId)
  return ok(
    call.tool_use_id,
    `Ticker set to ${ticker.toUpperCase()}${panelId ? ` on panel ${panelId}` : ''}`,
  )
}

function executeSetNewsSources(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.updatePanel) return fail(call.tool_use_id, 'updatePanel is not available')
  const sources = call.input.sources as string[]
  if (!sources || !Array.isArray(sources) || sources.length === 0)
    return fail(call.tool_use_id, 'sources must be a non-empty array of "news" and/or "sec"')
  const valid = new Set(['news', 'sec'])
  const invalid = sources.filter((s) => !valid.has(s))
  if (invalid.length > 0)
    return fail(
      call.tool_use_id,
      `Invalid sources: ${invalid.join(', ')}. Use "news" and/or "sec".`,
    )

  let panelId = call.input.panel_id as string | undefined
  if (!panelId) {
    const panels = callbacks.getPanelsByType?.('news') ?? []
    if (panels.length === 0) return fail(call.tool_use_id, 'No news panel found.')
    panelId = panels[0].panelId
  }
  callbacks.updatePanel(panelId, { sources })
  return ok(call.tool_use_id, `News sources set to ${sources.join(', ')} on panel ${panelId}`)
}

function executeSetFocusDate(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.setFocusDate) return fail(call.tool_use_id, 'setFocusDate is not available')
  const date = call.input.date as string | null
  if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date))
    return fail(call.tool_use_id, 'date must be in YYYY-MM-DD format or null')
  callbacks.setFocusDate(date)
  return ok(call.tool_use_id, date ? `Focus date set to ${date}` : 'Focus date reset to live')
}

function executeSetLinkMode(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.setLinkMode) return fail(call.tool_use_id, 'setLinkMode is not available')
  const panelId = call.input.panel_id as string
  const mode = call.input.mode as string
  if (!panelId) return fail(call.tool_use_id, 'panel_id is required')
  if (!mode) return fail(call.tool_use_id, 'mode is required')
  const validModes = new Set(['onChange', 'none', 'blue', 'green', 'yellow', 'purple'])
  if (!validModes.has(mode))
    return fail(call.tool_use_id, `Invalid mode "${mode}". Use: ${[...validModes].join(', ')}`)
  callbacks.setLinkMode(panelId, mode)
  return ok(call.tool_use_id, `Link mode set to "${mode}" on panel ${panelId}`)
}

/**
 * Park a tour behind the chat's Start walkthrough button: the slot it returns
 * rides the tool result to the companion, which holds it as the thread's
 * pending slot, and its answer carries it (spec 2026-10-18, 6.2), so a
 * reopened thread can start it.
 */
function parkWalkthrough(walkthrough: WalkthroughOffer): Pending {
  return { kind: 'walkthrough_offer', from: 'assistant', createdAt: Date.now(), walkthrough }
}

async function executeGetPlatformGuide(call: ToolCall): Promise<ToolResult> {
  const topic = call.input.topic as string
  if (!topic) return fail(call.tool_use_id, 'topic is required')
  const panelArg =
    typeof call.input.panel === 'string' && call.input.panel.trim() ? call.input.panel : undefined
  try {
    let data: {
      results?: Array<{
        slug?: string
        title: string
        content: string
        score: number
        steps?: WalkthroughStep[]
        /** The panels the entry is written for. */
        panels?: string[]
        /** Jev: does doing this walkthrough accomplish what was asked (0 to 1)? */
        walkthrough_fit?: number
      }>
    }
    try {
      // Pass the normalized panel so retrieval boosts same-panel + walkthrough
      // entries — the right how-to lands in the top 5 without widening it.
      data = await companionClient.post('/v1/knowledge/search', {
        query: topic,
        namespace: 'app_help',
        limit: 5,
        ...(panelArg ? { panel: normalizePanel(panelArg) } : {}),
      })
    } catch (err) {
      // Retrieval is the source of truth for what actually exists in the app. If
      // it can't confirm the steps, the model must NOT fall back to inventing a
      // UI path — it hedges and points the user at settings instead. (This is the
      // anti-confabulation guardrail: no grounding → no specific claims.)
      if (err instanceof ApiError) return ok(call.tool_use_id, UNVERIFIED_GUIDANCE)
      throw err
    }
    const results = data.results ?? []
    if (results.length === 0) {
      // No guide covers this. Never invent steps, but never stop at "not
      // documented" either: answer usefully, and the app offers the user a
      // "Request this" button for the missing guide.
      useDocGapStore.getState().record(topic)
      return ok(
        call.tool_use_id,
        `No how-to guide covers "${topic}" yet. Do NOT describe specific tabs, buttons, or steps you can't verify. Still be useful: in one plain line say you don't have a guide on that yet, then give the most helpful thing you can without inventing UI (what the feature or term means, the answer to the underlying question, or what you can do for them now). Keep it short. The app shows the user a "Request this" button for the missing guide; you may mention it.`,
      )
    }

    // Guided-walkthrough path: if a retrieved entry carries authored steps and
    // is a solid match, launch the deterministic (LLM-free) walkthrough and let
    // its terminal content BE the answer — no synthesis round.
    //
    // Pick the steps-entry with the highest RAW cosine (r.score is the true
    // cosine, pre-boost), not the first one in returned/boosted order. The
    // panel/steps boosts decide what lands in the top-N for the prose path, but
    // they must NOT decide the walkthrough: a same-panel but wrong-topic flow
    // (e.g. scanner.edit-columns) can out-boost the higher-cosine right-topic one
    // (columns-dashboard.color-code-column) and hijack it. Topic fidelity =
    // cosine. (Stopgap ahead of the action-catalog/select architecture — see
    // docs/superpowers/specs/2026-08-06-companion-action-catalog-design.md.)
    //
    // Similarity only finds candidates; whether one does what was asked is
    // Jev's call (`walkthrough_fit`), and the better fit wins. It must also run
    // on the panel the request names, by the rule that later anchors it.
    // 2026-10-18: "add RSI to a chart" parked the watchlist add-tickers tour
    // (0.39 beside the chart guide's 0.54); its button could never work.
    const best = Math.max(...results.map((r) => r.score))
    const askedPanel = panelArg ? normalizePanel(panelArg) : null
    const onAskedPanel = (panels: string[] | undefined) =>
      !askedPanel || !panels?.length || panels.some((p) => panelTypeMatches(p, askedPanel))
    const fits = (r: (typeof results)[number]) =>
      typeof r.walkthrough_fit === 'number'
        ? r.walkthrough_fit >= WALKTHROUGH_MIN_FIT
        : r.score >= best - WALKTHROUGH_MAX_GAP
    const guided = results
      .filter(
        (r) =>
          Array.isArray(r.steps) &&
          r.steps.length > 0 &&
          r.score >= WALKTHROUGH_MIN_SCORE &&
          onAskedPanel(r.panels) &&
          fits(r),
      )
      .sort((a, b) => (b.walkthrough_fit ?? 0) - (a.walkthrough_fit ?? 0) || b.score - a.score)[0]
    // A tour starts only when the user wants to do the task now. A question
    // about the feature (can it, how does it work, what does it mean) gets the
    // documentation, with the tour one click away: a cosine match alone once
    // launched the scanner filter tour on "can the scanner do X?" (2026-10-06).
    if (guided?.steps && call.input.wants_to_do_it !== true) {
      const pending = parkWalkthrough({
        steps: guided.steps,
        panel: panelArg ? normalizePanel(panelArg) : undefined,
        label: panelArg ? panelLabel(normalizePanel(panelArg)) : 'panel',
        slug: guided.slug,
      })
      const formatted = results.map((r) => `### ${r.title}\n${r.content}`).join('\n\n')
      return {
        ...ok(
          call.tool_use_id,
          `${GROUNDING_PREAMBLE}\n\n${formatted}\n\nA guided on-screen walkthrough of "${guided.title}" exists; the app shows a Start walkthrough button under your reply. Answer the question; mention the button only if doing it is the natural next step.`,
        ),
        pending,
      }
    }
    if (guided?.steps) {
      const anchor = resolveWalkthroughPanel(panelArg)
      if ('error' in anchor) {
        // Panel absent/ambiguous: push back instead of pointing at the wrong
        // panel, and park the flow so the chat can offer a "Start walkthrough"
        // button once the user has added or picked the panel. The assistant
        // never changes the workspace itself. Terminal content is spoken
        // verbatim.
        const pending = parkWalkthrough({
          steps: guided.steps,
          panel: panelArg ? normalizePanel(panelArg) : undefined,
          label: anchor.label,
          slug: guided.slug,
        })
        return { ...ok(call.tool_use_id, anchor.error, true), pending }
      }
      startWalkthrough(guided.steps, { panelId: anchor.panelId }, completionFor(guided.slug), {
        slug: guided.slug ?? null,
        source: 'chat',
      })
      // passthrough=true: this line IS the finished answer — spoken/shown
      // verbatim, no follow-up synthesis round. That keeps voice in sync with
      // the walkthrough that just launched (no ~5s lag) and stops the synthesis
      // step from rephrasing the authored intro into something inaccurate.
      const started = ok(
        call.tool_use_id,
        `Sure — I'll walk you through it on your ${anchor.label}. Follow my pointer: ${guided.steps[0].instruction.toLowerCase()}.`,
        true,
      )
      // The tour runs now, so one parked earlier in the turn is no longer on
      // offer: the companion withdraws it from the thread's slot.
      return { ...started, withdraws: 'walkthrough_offer' }
    }

    const formatted = results.map((r) => `### ${r.title}\n${r.content}`).join('\n\n')
    return ok(call.tool_use_id, `${GROUNDING_PREAMBLE}\n\n${formatted}`)
  } catch (err) {
    void err
    return ok(call.tool_use_id, UNVERIFIED_GUIDANCE)
  }
}

function executeReadPanelState(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  let panelId = call.input.panel_id as string | undefined
  const panelType = call.input.panel_type as string | undefined
  let state: Record<string, unknown> | null = null

  // Resolve panel by type if no ID given
  if (!panelId) {
    const searchType = panelType ?? 'scanner'
    const panels = callbacks.getPanelsByType?.(searchType) ?? []
    if (panels.length === 0)
      return fail(call.tool_use_id, `No ${searchType} panel found on this page.`)
    if (panels.length > 1) {
      // Refuse to silently pick one — the caller has to specify which panel,
      // otherwise back-to-back tool calls (e.g. add_panel then read_panel_state)
      // can read the wrong panel and corrupt downstream reasoning.
      return fail(
        call.tool_use_id,
        `Multiple ${searchType} panels found (${panels.map((p) => p.panelId).join(', ')}). Specify panel_id, or call request_panel_selection to let the user choose.`,
      )
    }
    panelId = panels[0].panelId
    // What the panel shows on screen, as a read by id gives: a chart grid's stored
    // state carries a panel-level period that no cell may show.
    state = getPanelLiveState(panelId) ?? panels[0].state
  }

  // Try live state registry first (panels register their current state on render)
  if (!state) {
    state = getPanelLiveState(panelId!)
  }

  // Fall back to workspace store
  if (!state && callbacks.getPanel) {
    state = callbacks.getPanel(panelId!)
  }

  if (!state) {
    // Panel state is null — scan config may not have been written to panel state yet.
    // Try reading all available data for this panel from layout + window config.
    const layout = callbacks.getLayout?.()
    const windowConfig = layout?.windowConfigs?.[panelId!] as Record<string, unknown> | undefined

    const lines = [
      `Panel: ${panelId} (panel state is empty — this scanner is using default/unsaved configuration)`,
    ]
    if (windowConfig) {
      lines.push(`Window config: ${JSON.stringify(windowConfig)}`)
    }
    lines.push(
      'The scanner filters may not have been explicitly saved to panel state. The user may need to modify a filter for it to appear in the panel state.',
    )
    return ok(call.tool_use_id, lines.join('\n'))
  }

  // Return the full panel state so Claude can read all fields
  const lines: string[] = [`Panel: ${panelId}`, `State keys: ${Object.keys(state).join(', ')}`]

  // Dump all state as readable key-value pairs
  for (const [key, val] of Object.entries(state)) {
    if (val == null) continue
    if (typeof val === 'object') {
      lines.push(`${key}: ${JSON.stringify(val)}`)
    } else {
      lines.push(`${key}: ${val}`)
    }
  }

  // Guide Claude on which tool to use based on existing state
  const hasFilters =
    state.filters &&
    (Array.isArray(state.filters)
      ? (state.filters as unknown[]).length > 0
      : Object.keys(state.filters as Record<string, unknown>).length > 0)
  if (hasFilters) {
    lines.push(
      '\nNOTE: This panel has existing filters. Use set_filters for both adding and updating — only send the columns you want to change. Filters on columns you do NOT send are preserved automatically. There is no separate "add" vs "update" tool.',
    )
  }

  return ok(call.tool_use_id, lines.join('\n'))
}

function executeReadCurrentGraph(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.getNodes || !callbacks.getEdges)
    return fail(call.tool_use_id, 'graph access is not available on this page')
  const { nodes, edges } = aiGraphView(
    callbacks.getNodes() as unknown as GraphNode[],
    callbacks.getEdges() as unknown as GraphEdge[],
  )
  if (nodes.length === 0) return ok(call.tool_use_id, 'Graph is empty — no nodes.')
  // Recipe form: the same compact language create_strategy_from_recipe consumes
  // (~10× fewer tokens than a node/edge dump, and directly editable).
  const { recipe } = graphToRecipe(nodes, edges)
  return ok(
    call.tool_use_id,
    `Strategy graph: ${nodes.length} nodes, ${edges.length} edges. Recipe form (valid create_strategy_from_recipe input):\n${JSON.stringify(recipe)}`,
  )
}

function executeGetNodeSpecs(call: ToolCall, _callbacks: ActionCallbacks): ToolResult {
  const types = call.input.types as string[]
  if (!types || !Array.isArray(types) || types.length === 0)
    return fail(call.tool_use_id, 'types (non-empty string array) is required')
  return ok(call.tool_use_id, describeNodeTypes(types))
}

async function executeReadBacktestResults(
  call: ToolCall,
  callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  const result = callbacks.getBacktestResults?.()
  if (!result)
    return ok(call.tool_use_id, 'No current backtest results available. Run a backtest first.')
  const summary = {
    executionId: result.execution_id,
    ticker: result.ticker,
    start: result.start,
    end: result.end,
    metrics: result.execution_summary ?? result.portfolio ?? null,
  }
  if (call.input.include_trades === true) {
    const trades = await executeAnalyzeBacktest(
      {
        ...call,
        name: 'analyze_backtest',
        input: {
          executionId: result.execution_id,
          artifact: 'transactions',
          operation: 'trade_summary',
        },
      },
      callbacks,
      turn,
    )
    if (!trades.success) return trades
    const analysis = JSON.parse(trades.content)
    return ok(call.tool_use_id, serializeGraphResult({ ...summary, tradeAnalysis: analysis }))
  }
  return ok(call.tool_use_id, serializeGraphResult(summary))
}

function executeCreateStrategyFromRecipe(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.setNodesAndEdges)
    return fail(call.tool_use_id, 'setNodesAndEdges is not available on this page')
  const recipe = call.input.recipe as Array<Record<string, unknown>>
  if (!recipe || !Array.isArray(recipe) || recipe.length === 0)
    return fail(call.tool_use_id, 'recipe (non-empty array) is required')
  if (!callbacks.buildFromRecipe)
    return fail(call.tool_use_id, 'buildFromRecipe is not available on this page')
  const result = callbacks.buildFromRecipe(recipe)
  if (!result.success) {
    const lines = result.engineErrors?.length
      ? result.engineErrors.map(formatEngineError)
      : result.errors
    return fail(call.tool_use_id, `Recipe build failed:\n${lines.join('\n')}`)
  }

  // Full-graph validation gate: renderable errors block the commit and go
  // back to the model as the repair payload; backtestable warnings surface
  // as advisories without blocking. The editor's readiness: the engine view
  // (Trade Managers lowered, issues mapped back to the node the model wrote)
  // plus the whole-strategy rules (one Trade Manager, one Report, one
  // universe). A recipe's node ids are its step ids, so the payload names
  // steps the model knows.
  const report = computeGraphReadiness(
    result.nodes as unknown as Node<NodeData>[],
    result.edges as unknown as Edge[],
  )
  if (report.errors.length > 0) {
    return fail(
      call.tool_use_id,
      `Recipe produced an invalid graph — fix and retry:\n${report.errors.map(formatEngineError).join('\n')}`,
    )
  }

  const willAppend = call.input.append === true && !!callbacks.appendNodesAndEdges
  recordGraphEdit({
    title: willAppend ? 'Strategy appended' : 'Strategy built',
    diffLines: buildBuildSummary(result.nodes.length, willAppend),
    commit: () => applyBuiltGraph(call, callbacks, result.nodes, result.edges),
  })
  const appended = willAppend
  const advisories =
    report.warnings.length > 0
      ? `\nAdvisories (non-blocking):\n${report.warnings.map(formatEngineError).join('\n')}`
      : ''
  return ok(
    call.tool_use_id,
    `Strategy ${appended ? 'appended' : 'built'} from recipe: ${result.nodes.length} nodes, ${result.edges.length} edges${advisories}`,
  )
}

/**
 * apply_graph_ops — the model's batch edit path. Runs the op batch through the
 * graph engine against the CURRENT graph: transactional (nothing half-applies)
 * and post-validated by applyGraphOps itself (errors the batch INTRODUCES block
 * the commit; pre-existing issues elsewhere don't). On failure the EngineErrors
 * go back verbatim as the repair payload — the shared tool loop drives retries,
 * same as create_strategy_from_recipe. Ops reference recipe step ids (the form
 * read_current_graph shows the model); the engine resolves them via the idMap.
 */
function executeApplyGraphOps(call: ToolCall, callbacks: ActionCallbacks): ToolResult {
  if (!callbacks.getNodes || !callbacks.getEdges)
    return fail(call.tool_use_id, 'graph access is not available on this page')
  if (!callbacks.setNodesAndEdges)
    return fail(call.tool_use_id, 'setNodesAndEdges is not available on this page')
  const ops = call.input.ops as GraphOp[] | undefined
  if (!ops || !Array.isArray(ops) || ops.length === 0)
    return fail(call.tool_use_id, 'ops (non-empty array) is required')

  const nodes = callbacks.getNodes() as unknown as GraphNode[]
  const edges = callbacks.getEdges() as unknown as GraphEdge[]
  // The ids read_current_graph showed the model: the same view, the same order.
  const view = aiGraphView(nodes, edges)
  const { idMap } = graphToRecipe(view.nodes, view.edges)

  const result = applyGraphOps({ nodes, edges }, ops, idMap)
  if (!result.ok) {
    return fail(
      call.tool_use_id,
      `apply_graph_ops failed — graph unchanged. Fix and retry:\n${result.errors
        .map(formatEngineError)
        .join('\n')}`,
    )
  }

  const { graph, warnings } = result.value

  // Diff card + one-step undo snapshot + node pulse (H2 item 2). recordGraphEdit
  // captures the pre-batch snapshot BEFORE committing so Cmd+Z and card-Undo
  // treat the whole batch as a single step.
  const diffLines = buildEditDiff(ops, { nodes, edges })
  recordGraphEdit({
    title: 'Strategy edited',
    diffLines,
    affectedIds: affectedNodeIds(ops, result.value.idMap),
    commit: () =>
      callbacks.setNodesAndEdges!(
        graph.nodes as unknown as Array<Record<string, unknown>>,
        graph.edges as unknown as Array<Record<string, unknown>>,
      ),
  })

  const advisories =
    warnings.length > 0
      ? `\nAdvisories (non-blocking):\n${warnings.map(formatEngineError).join('\n')}`
      : ''
  return ok(
    call.tool_use_id,
    `Applied ${ops.length} op(s): ${graph.nodes.length} nodes, ${graph.edges.length} edges${advisories}`,
  )
}

/**
 * rerun_backtest — trigger a re-run of the current strategy. Delegates to the
 * `rerunBacktest` callback, which on the editor page fires the same single-run
 * path Cmd+Enter/auto-run uses (the editor never populates the batch run-setup
 * config Run mode reads as its prefill). Missing callback → capability
 * error; empty/unrunnable graph → the callback's guidance message, surfaced as
 * a non-error result (no repair loop). Never throws.
 */
async function executeRerunBacktest(
  call: ToolCall,
  callbacks: ActionCallbacks,
): Promise<ToolResult> {
  if (!callbacks.rerunBacktest)
    return fail(
      call.tool_use_id,
      'Re-run is not available on this page. Ask the user to run the strategy from the editor first.',
    )
  const result = await callbacks.rerunBacktest()
  return result.ok ? ok(call.tool_use_id, result.message) : fail(call.tool_use_id, result.message)
}

async function executePublicGraph(call: ToolCall): Promise<ToolResult> {
  const api = getActiveGraphApi()
  if (!api)
    return fail(
      call.tool_use_id,
      'No active strategy editor. Open a strategy and read its graph first.',
    )
  const invoke = () => executeGraphMethod(api, call.name as 'graph_read' | 'graph_call', call.input)
  const result = GRAPH_EDIT_METHODS.has(String(call.input.method))
    ? await recordAsyncGraphEdit(`Graph: ${String(call.input.method)}`, invoke)
    : await invoke()
  if (
    call.input.method === 'run' &&
    result &&
    typeof result === 'object' &&
    'status' in result &&
    result.status !== 'completed'
  ) {
    return fail(call.tool_use_id, serializeGraphResult(result))
  }
  return ok(call.tool_use_id, serializeGraphResult(result))
}

async function executeNavigateTo(
  call: ToolCall,
  callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  if (!callbacks.navigate) {
    return fail(call.tool_use_id, 'navigate is not available on this page')
  }
  const routeId = call.input.route_id as string
  if (!routeId) {
    return fail(call.tool_use_id, 'route_id is required')
  }
  const params = (call.input.params as Record<string, string> | undefined) ?? {}
  const result = resolveRoute(routeId, params)
  if ('error' in result) {
    const validIds = ROUTE_CATALOG.map((e) => e.id).join(', ')
    return fail(call.tool_use_id, `${result.error}. Valid route ids: ${validIds}`)
  }
  // Guard (2026-07-23): from INSIDE the strategy editor, hopping to the
  // backtester's own surfaces is always a misfire — "build me X" prompts have
  // made the model navigate_to the strategies list, dumping the user out of
  // the editor mid-request. Failing with guidance keeps the tool loop alive
  // so the model self-corrects to the graph tools instead.
  const here = window.location.pathname
  if (
    (here.startsWith('/backtester/editor') || /^\/backtester\/strategy\/[^/]+\/build/.test(here)) &&
    (result.path === '/backtester' || result.path.startsWith('/backtester/editor'))
  ) {
    return fail(
      call.tool_use_id,
      'Already in the backtest strategy editor — do not navigate. Build or edit the strategy right here: load create_strategy_from_recipe (new strategy) or apply_graph_ops (edits) via get_tools, then execute.',
    )
  }
  callbacks.navigate(result.path)
  if (routeId === 'backtest-editor') {
    const accountId = useAccountStore.getState().session?.user?.sub
    const deadline = Date.now() + 8_000
    while (Date.now() < deadline) {
      if (turn?.signal.aborted || useAccountStore.getState().session?.user?.sub !== accountId) {
        return fail(call.tool_use_id, 'Editor navigation stopped; no graph action was dispatched.')
      }
      const api = getActiveGraphApi()
      try {
        if (
          api?.getStrategyInfo().id === params.id &&
          useActionRegistrationStore.getState().callbacks?.getNodes
        ) {
          // A mounted editor can still be hydrating or read-only.
          assertActiveGraphWritable()
          return ok(
            call.tool_use_id,
            `Opened strategy ${params.id}. Read its current graph before editing.`,
          )
        }
      } catch {
        /* Wait for the requested authenticated editor to finish loading. */
      }
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
    return fail(
      call.tool_use_id,
      'Requested editor is not ready or is read-only. No graph action was dispatched.',
    )
  }
  return ok(call.tool_use_id, `Navigated to ${routeId} (${result.path})`)
}

// ── Backend tool executor (search_knowledge) ────────────────

interface KnowledgeHit {
  title: string
  domain: string
  score: number
  description: string
  config: unknown
}
interface KnowledgeConcept {
  query?: string
  results?: KnowledgeHit[]
  vocabulary?: Array<{ term: string; meaning: string }>
}

function formatKnowledge(c: KnowledgeConcept): string {
  const formatted = (c.results || [])
    .map(
      (r) =>
        `**${r.title}** (${r.domain}, score: ${r.score.toFixed(2)})\n${r.description}\nConfig: ${JSON.stringify(r.config)}`,
    )
    .join('\n\n---\n\n')
  // Trader shorthand the server resolved exactly ("FRD", "momo"): lead with
  // what it means so the model builds from the right concept.
  const terms = (c.vocabulary || []).map((v) => `- ${v.term}: ${v.meaning}`).join('\n')
  const header = terms ? `Trader terms matched:\n${terms}\n\n` : ''
  return header + (formatted || NO_RECIPE_MATCH)
}

async function executeSearchKnowledge(call: ToolCall): Promise<ToolResult> {
  const query = call.input.query as string | undefined
  const queries = Array.isArray(call.input.queries)
    ? (call.input.queries as unknown[]).filter((q): q is string => typeof q === 'string' && !!q)
    : []
  if (!query && !queries.length) return fail(call.tool_use_id, 'query or queries is required')

  try {
    let data: KnowledgeConcept & { concepts?: KnowledgeConcept[] }
    try {
      data = await companionClient.post('/v1/companion/search-knowledge', {
        // Several concepts go in one call: the server searches them in parallel.
        ...(queries.length ? { queries } : { query }),
        domain: call.input.domain,
        panel_type: call.input.panel_type,
        limit: call.input.limit ?? 10,
      })
    } catch (err) {
      if (err instanceof ApiError) {
        return fail(call.tool_use_id, `Knowledge search failed: ${err.status}`)
      }
      throw err
    }
    if (data.concepts) {
      return ok(
        call.tool_use_id,
        data.concepts.map((c) => `## ${c.query}\n\n${formatKnowledge(c)}`).join('\n\n'),
      )
    }
    return ok(call.tool_use_id, formatKnowledge(data))
  } catch (err) {
    return fail(
      call.tool_use_id,
      `Knowledge search error: ${err instanceof Error ? err.message : String(err)}`,
    )
  }
}

// ── Backend tool executor (ask_analyst) ─────────────────────

/** Per-ask counter so each analyst ask groups its rounds under one debug tab. */
let analystAskSeq = 0

/**
 * Map an analyst `round_usage` SSE payload into the generic `LlmCallMeta`
 * envelope, folding running conversation totals into `session` (mutated in
 * place). Mirrors the companion round mapper; the analyst's model round is
 * `llm_ms`, its tool time is server-attributed to the requesting round.
 */
function analystRoundToMeta(
  askId: string,
  raw: unknown,
  session: LlmSessionTotals,
  feature = 'analyst',
): LlmCallMeta {
  const d = raw as {
    round?: number
    model_served?: string
    models_attempted?: string[]
    tokens_in?: number | null
    tokens_out?: number | null
    cached_tokens?: number | null
    cost?: number | null
    llm_ms?: number
    tool_ms?: number
    ttft_ms?: number | null
    input?: unknown
    output?: unknown
  }
  const totalMs = d.llm_ms ?? 0
  const toolMs = d.tool_ms ?? 0
  const tokensIn = typeof d.tokens_in === 'number' ? d.tokens_in : undefined
  const tokensOut = typeof d.tokens_out === 'number' ? d.tokens_out : undefined
  const costUsd = typeof d.cost === 'number' ? d.cost : undefined
  session.rounds += 1
  session.modelMs += totalMs
  session.toolMs += toolMs
  session.tokensIn = (session.tokensIn ?? 0) + (tokensIn ?? 0)
  session.tokensOut = (session.tokensOut ?? 0) + (tokensOut ?? 0)
  session.costUsd = (session.costUsd ?? 0) + (costUsd ?? 0)
  return {
    feature,
    conversationId: askId,
    correlationId: askId,
    round: d.round ?? 0,
    modelServed: d.model_served || undefined,
    modelsAttempted: d.models_attempted,
    ttftMs: typeof d.ttft_ms === 'number' ? d.ttft_ms : undefined,
    totalMs,
    toolMs,
    tokensIn,
    tokensOut,
    cachedTokens: typeof d.cached_tokens === 'number' ? d.cached_tokens : undefined,
    costUsd,
    input: d.input ?? undefined,
    output: d.output ?? undefined,
    ok: true,
    session: { ...session },
  }
}

/**
 * Ask the server-side research analyst a data question. Unlike the other
 * backend tool (search_knowledge, which hits the companion `apiBase` on
 * edge-llm-api), the analyst lives on the edge-research service, so this
 * targets companion-api directly. The analyst runs a server-side agentic
 * tool loop over the market-data tools and returns Markdown prose with
 * citations, which we surface verbatim as the tool result.
 */
/**
 * Shared analyst streaming core: POST /v1/analyst/ask/stream, relay deltas
 * live, surface hard billing/availability failures verbatim, emit per-round
 * cost to the dev `ai` channel, and return the full text as the tool result.
 * Used by both ask_analyst and summarize_history.
 */
async function streamAnalyst(
  params: {
    toolUseId: string
    question: string
    ticker?: string
    date?: string
    context?: string
    /** Debug-channel feature label + key prefix (one sidebar tab per ask). */
    feature?: string
    keyPrefix?: string
    signal?: AbortSignal
  },
  onDelta?: (text: string) => void,
): Promise<ToolResult> {
  const feature = params.feature ?? 'analyst'
  const askId = `${params.keyPrefix ?? 'analyst'}-${++analystAskSeq}`
  const session: LlmSessionTotals = {
    rounds: 0,
    modelMs: 0,
    toolMs: 0,
    tokensIn: 0,
    tokensOut: 0,
    costUsd: 0,
  }

  // On a voice turn the analyst's answer is what the trader hears (the manager
  // speaks it as it is), so it is asked for as speech, at the voice tier the
  // direct route uses (Chris, 2026-08-28: voice runs the fast tier).
  const spoken = currentTurnModality() === 'voice'
  // Derive effective tier: read stored preference and clamp to plan allowance.
  // tool-executor is not a component, so we read both stores via getState().
  const storedTier = useTierStore.getState().tier
  const planTier = getResolvedPlanTier()
  const effectiveTier = spoken ? VOICE_ANALYST_TIER : clampTierToPlan(storedTier, planTier)

  try {
    // Stream the answer so first words appear as they're written. Each
    // `answer_delta` is relayed live via onDelta; the full text is still
    // returned as the tool result (with answer_event_id for feedback).
    const turnPanels = currentTurnPanels()
    const turnToken = currentTurnToken()
    const clientTurnId = currentClientTurnId()
    const threadId = useThreadStore.getState().activeThreadId
    const screenLists = onScreenWatchlists()
    let response: Response
    try {
      response = await companionClient.stream(
        '/v1/analyst/ask/stream',
        {
          question: params.question,
          ticker: params.ticker,
          date: params.date,
          context: params.context,
          // Panels the user @-mentioned this turn ride as structured evidence
          // regardless of what the chat model chose to write into `context`.
          ...(turnPanels?.length ? { panels: turnPanels } : {}),
          // Force the analyst's answer language only when the user chose one;
          // a browser-inferred default leaves it to follow the question.
          language: forcedLanguageName(),
          tier: effectiveTier,
          ...(spoken ? { modality: 'voice' } : {}),
          // The companion turn this answer serves and its persona, so its cost
          // joins that turn in analytics (cost per persona and per turn).
          persona: ASSISTANT_PERSONA,
          ...(turnToken ? { turn_token: turnToken } : {}),
          // The companion turn's id, so the analyst's analyst_turn joins it.
          ...(clientTurnId ? { client_turn_id: clientTurnId } : {}),
          // The chat thread, so analytics files the answer under its
          // conversation ($ai_session_id). Analytics only.
          ...(threadId ? { session_id: threadId } : {}),
          // "My watchlist" is the one on screen when there is one (watchlist tools).
          ...(screenLists.length ? { on_screen_watchlists: screenLists } : {}),
          // Request the dev prompt-echo; the server still double-gates it.
          debug: debugToolsEnabled,
          // EdgeScript tools and the edgescript capability, only for a user with
          // `edgescript-release` in a build that contains EdgeScript.
          ...(EDGESCRIPT_ENABLED && isFeatureFlagOn(FLAGS.EDGESCRIPT) ? { edgescript: true } : {}),
        },
        { signal: params.signal },
      )
    } catch (err) {
      if (!(err instanceof ApiError)) throw err
      // Hard billing / availability failures get a deterministic, user-friendly
      // message shown VERBATIM (passthrough=true) — never a raw status code, and
      // never re-phrased by the model.
      if (err.status === 402) {
        return fail(params.toolUseId, ALLOWANCE_SPENT_MESSAGE, true)
      }
      if (err.status === 429) {
        return fail(
          params.toolUseId,
          'The AI is handling a lot of requests right now. Please try again in a moment.',
          true,
        )
      }
      if (err.status === 503) {
        return fail(
          params.toolUseId,
          'AI answers are temporarily unavailable. Please try again shortly.',
          true,
        )
      }
      // Other failures: a generic friendly message (no raw status leak); left
      // non-passthrough so the model can still add context if useful.
      return fail(params.toolUseId, 'The analyst could not complete this request.')
    }
    if (!response.body) {
      return fail(params.toolUseId, 'The analyst could not complete this request.')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let fullText = ''
    let answerEventId: string | undefined
    let errorMessage: string | undefined
    // On a voice turn the relayed text is spoken as it streams. Once the
    // server withdraws the draft (answer_reset), the fix is the tool result,
    // shown and not relayed to be heard twice (2026-10-22).
    let relay = true

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      // Shared SSE frame parser (same implementation the companion chat loop
      // uses) — handles the \r\n\r\n / \n\n boundary + JSON parsing.
      const { frames, remaining } = drainFramesFromBuffer(buffer)
      buffer = remaining
      for (const frame of frames) {
        const parsed = parseSseFrame(frame)
        if (!parsed) continue
        const data = parsed.data as {
          text?: string
          answer_event_id?: string
          message?: string
        }
        if (parsed.event === 'answer_delta') {
          fullText += data.text ?? ''
          if (data.text && relay) onDelta?.(data.text)
        } else if (parsed.event === 'answer_reset') {
          // The server's answer guard withdrew the draft; only the fix is the
          // tool result (the live text is replaced by the final message).
          fullText = ''
          if (spoken) relay = false
        } else if (parsed.event === 'round_usage') {
          if (debugToolsEnabled) {
            emitAiDebugRound(askId, analystRoundToMeta(askId, parsed.data, session, feature))
          }
        } else if (parsed.event === 'done') {
          answerEventId = data.answer_event_id ?? undefined
        } else if (parsed.event === 'error') {
          errorMessage = data.message
        }
      }
    }

    if (errorMessage) return fail(params.toolUseId, errorMessage)
    const result = ok(params.toolUseId, fullText || 'The analyst returned no answer.')
    if (answerEventId) result.answerEventId = answerEventId
    return result
  } catch (err) {
    return fail(
      params.toolUseId,
      `Analyst error: ${err instanceof Error ? err.message : String(err)}`,
    )
  }
}

/**
 * ask_analyst — a market-data question about a specific ticker/event. Thin
 * wrapper over the shared analyst stream.
 */
async function executeAskAnalyst(
  call: ToolCall,
  onDelta?: (text: string) => void,
  signal?: AbortSignal,
): Promise<ToolResult> {
  const question = call.input.question as string
  if (!question) return fail(call.tool_use_id, 'question is required')
  return streamAnalyst(
    {
      toolUseId: call.tool_use_id,
      question,
      ticker: call.input.ticker as string | undefined,
      date: call.input.date as string | undefined,
      signal,
    },
    onDelta,
  )
}

/** Read the columns from the first history-records panel in the workspace. */
function readHistoryRecordsColumns(callbacks: ActionCallbacks): string[] {
  const windowConfigs = callbacks.getLayout?.()?.windowConfigs as
    | Record<string, { componentType?: string }>
    | undefined
  if (!windowConfigs) return []
  for (const [panelId, cfg] of Object.entries(windowConfigs)) {
    if (cfg?.componentType !== 'history-records') continue
    const cols =
      (getPanelLiveState(panelId) as { selectedColumns?: string[] } | undefined)?.selectedColumns ??
      (callbacks.getPanel?.(panelId) as { selectedColumns?: string[] } | undefined)?.selectedColumns
    if (cols && cols.length > 0) return cols
  }
  return []
}

/** Build the analyst `context` describing the current History filtered set. */
function buildHistoryContext(historyFilters: unknown, columns: string[]): string {
  const lines: string[] = [
    'This is a "summarize the current History page" request. Analyze the ' +
      'POPULATION of rows matching the active History filters below by writing ' +
      'query_motherduck SQL (translate each filter leaf to a WHERE condition; ' +
      'the leaf column keys match the schema). Keep queries aggregate + bounded.',
  ]
  const filtersObj = (historyFilters as { filters?: unknown })?.filters ?? historyFilters
  const hasFilters =
    !!filtersObj &&
    (Array.isArray(filtersObj)
      ? filtersObj.length > 0
      : Object.keys(filtersObj as object).length > 0)
  lines.push(
    hasFilters
      ? `Active filters (JSON): ${JSON.stringify(filtersObj)}`
      : 'No active filters, so the population is the full dataset; add a bounded date range to keep the scan fast.',
  )
  if (columns.length > 0) {
    lines.push(`Columns the user is viewing (what they care about): ${columns.join(', ')}`)
  }
  return lines.join('\n')
}

/**
 * summarize_history: summarize/analyze the History page's CURRENT filtered
 * population. Reads the active filters + viewed columns from the workspace and
 * hands them to the (warehouse-capable) analyst as grounding context.
 */
async function executeSummarizeHistory(
  call: ToolCall,
  callbacks: ActionCallbacks,
  onDelta?: (text: string) => void,
  signal?: AbortSignal,
): Promise<ToolResult> {
  const historyFilters = (callbacks.getGlobalSettings?.() as Record<string, unknown> | undefined)
    ?.historyFilters
  const columns = readHistoryRecordsColumns(callbacks)
  const focus = call.input.focus as string | undefined
  const question = focus
    ? `Summarize the current filtered History population, focusing on: ${focus}.`
    : 'Summarize the notable patterns in the current filtered History population: what stands out, the biggest movers, and any concentration by sector, day, or setup.'
  return streamAnalyst(
    {
      toolUseId: call.tool_use_id,
      question,
      context: buildHistoryContext(historyFilters, columns),
      feature: 'history-summary',
      keyPrefix: 'history-summary',
      signal,
    },
    onDelta,
  )
}

/**
 * Change the response language (a preference, not a workspace mutation — hence
 * read-only, no approve/revert). The app-layout effect mirrors the new value
 * into the manager so subsequent replies force it.
 */
function executeSetLanguage(call: ToolCall): ToolResult {
  const code = call.input.language as string
  if (code !== 'en' && code !== 'es') {
    return fail(call.tool_use_id, `Unsupported language: ${code}. Use "en" or "es".`)
  }
  useLanguageStore.getState().setLanguage(code as LanguageCode)
  return ok(call.tool_use_id, `Response language set to ${languageName(code as LanguageCode)}.`)
}

// ── Dispatch ────────────────────────────────────────────────

function savedStrategyId(input: Record<string, unknown>): string {
  const id = input.strategyId
  if (typeof id !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(id))
    throw new Error('A valid strategyId is required.')
  return id
}

function savedStrategyLink(id: string): string {
  return `/backtester/strategy/${encodeURIComponent(id)}/build`
}

function assertSavedInput(input: Record<string, unknown>, allowed: string[]): void {
  if (Object.keys(input).some((key) => !allowed.includes(key)))
    throw new Error('Unknown saved strategy argument.')
  if (JSON.stringify(input).length > 128_000)
    throw new Error('Saved strategy request is too large.')
}

async function executeReadSavedStrategy(
  call: ToolCall,
  _callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  assertSavedInput(call.input, ['strategyId'])
  const accountId = useAccountStore.getState().session?.user?.sub
  const strategy = await readSavedStrategyGraph(savedStrategyId(call.input))
  if (
    !accountId ||
    useAccountStore.getState().session?.user?.sub !== accountId ||
    turn?.signal.aborted
  )
    throw new Error('Account or task changed; start a new request.')
  const view = aiGraphView(strategy.nodes, strategy.edges)
  const { recipe } = graphToRecipe(view.nodes, view.edges)
  return ok(
    call.tool_use_id,
    serializeGraphResult({
      strategyId: strategy.id,
      name: strategy.name,
      graphRevision: strategy.graphRevision ?? 0,
      nodeCount: strategy.nodes.length,
      edgeCount: strategy.edges.length,
      recipe,
      buildUrl: savedStrategyLink(strategy.id),
    }),
  )
}

async function executeCreateSavedStrategy(
  call: ToolCall,
  _callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  if (!turn) throw new Error('A current AI task is required.')
  assertSavedInput(call.input, ['name', 'recipe'])
  const name = call.input.name
  const recipe = call.input.recipe
  if (typeof name !== 'string' || !name.trim() || name.length > 120)
    throw new Error('A strategy name of at most 120 characters is required.')
  if (
    !Array.isArray(recipe) ||
    recipe.length === 0 ||
    recipe.length > 200 ||
    recipe.some(
      (step) =>
        !step ||
        typeof step !== 'object' ||
        Array.isArray(step) ||
        typeof step.id !== 'string' ||
        typeof step.type !== 'string',
    )
  )
    throw new Error('A bounded recipe is required.')
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  const strategy = await createSavedStrategyFromRecipe({
    name: name.trim(),
    recipe: recipe as Recipe,
  })
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  return ok(
    call.tool_use_id,
    JSON.stringify({
      strategyId: strategy.id,
      name: strategy.name,
      graphRevision: strategy.graphRevision ?? 0,
      buildUrl: savedStrategyLink(strategy.id),
    }),
  )
}

async function executeEditSavedStrategy(
  call: ToolCall,
  _callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  if (!turn) throw new Error('A current AI task is required.')
  assertSavedInput(call.input, ['strategyId', 'expectedGraphRevision', 'ops'])
  const strategyId = savedStrategyId(call.input)
  const expectedGraphRevision = call.input.expectedGraphRevision
  const ops = call.input.ops
  if (!Number.isInteger(expectedGraphRevision) || (expectedGraphRevision as number) < 0)
    throw new Error('A non-negative expectedGraphRevision is required.')
  if (
    !Array.isArray(ops) ||
    ops.length === 0 ||
    ops.length > 100 ||
    ops.some(
      (op) => !op || typeof op !== 'object' || Array.isArray(op) || typeof op.op !== 'string',
    )
  )
    throw new Error('A bounded graph operation list is required.')
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  const result = await applySavedGraphOps({
    strategyId,
    expectedGraphRevision: expectedGraphRevision as number,
    ops: ops as GraphOp[],
  })
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  // Undo writes back the graph this edit replaced (2026-10-15).
  recordSavedStrategyEdit(call.tool_use_id, {
    strategyId: result.strategy.id,
    previous: result.previous,
    revision: result.strategy.graphRevision ?? 0,
  })
  return ok(
    call.tool_use_id,
    serializeGraphResult({
      strategyId: result.strategy.id,
      graphRevision: result.strategy.graphRevision ?? 0,
      idMap: result.idMap,
      warnings: result.warnings,
      buildUrl: savedStrategyLink(result.strategy.id),
    }),
  )
}

async function executeRunSavedStrategy(
  call: ToolCall,
  _callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  if (!turn) throw new Error('A current AI task is required.')
  assertSavedInput(call.input, [
    'strategyId',
    'graphRevision',
    'ticker',
    'targetDate',
    'initialCash',
  ])
  const strategyId = savedStrategyId(call.input)
  const revision = call.input.graphRevision
  const ticker = call.input.ticker
  const targetDate = call.input.targetDate
  const initialCash = call.input.initialCash ?? 100_000
  if (!Number.isInteger(revision) || (revision as number) < 0)
    throw new Error('A non-negative graphRevision is required.')
  if (typeof ticker !== 'string' || !/^[A-Za-z0-9.^_-]{1,20}$/.test(ticker))
    throw new Error('A valid ticker is required.')
  if (typeof targetDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(targetDate))
    throw new Error('Target date must be YYYY-MM-DD.')
  if (typeof initialCash !== 'number' || !Number.isFinite(initialCash) || initialCash <= 0)
    throw new Error('initialCash must be positive.')
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  const strategy = await readSavedStrategyGraph(strategyId)
  if (strategy.script || strategy.uiState?.editorMode === 'code')
    throw new Error('Code-mode strategies cannot be run with saved graph tools.')
  if ((strategy.graphRevision ?? 0) !== revision)
    throw new Error('Strategy graph revision changed; read_saved_strategy again.')
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  const result = await runHeadlessSingleRun(
    {
      strategyId,
      graphRevision: revision as number,
      graph: { nodes: strategy.nodes, edges: strategy.edges },
      ticker,
      targetDate,
      initialCash,
    },
    turn,
  )
  assertAutomationTurnCurrent(turn, undefined, false, { allowHeadless: true })
  if (result.receipt.status === 'completed' && result.receipt.executionId && result.response) {
    const accountId = useAccountStore.getState().session?.user?.sub
    if (accountId)
      setHeadlessExecutionContext({
        turnId: turn.id,
        accountId,
        strategyId,
        executionId: result.receipt.executionId,
        result: {
          execution_id: result.receipt.executionId,
          transaction_file_url: result.response.transactions_uri,
          debug_file_url: result.response.debug_file_url,
        },
      })
  }
  const content = JSON.stringify({ ...result.receipt, buildUrl: savedStrategyLink(strategyId) })
  return result.receipt.status === 'completed'
    ? ok(call.tool_use_id, content)
    : fail(call.tool_use_id, content)
}

type ToolExecutor = (
  call: ToolCall,
  callbacks: ActionCallbacks,
  automation?: AutomationTurn,
) => ToolResult | Promise<ToolResult>

// Feature code that must stay out of user bundles (EdgeScript, dev-gated) registers
// its executors here at runtime, next to its schemas (`registerToolDefinition`).
const RUNTIME_EXECUTORS = new Map<string, ToolExecutor>()

export function registerToolExecutor(name: string, executor: ToolExecutor): void {
  RUNTIME_EXECUTORS.set(name, executor)
}

/** A runtime-registered executor (read_chart for the chart prefetch); undefined when not registered. */
export function runtimeToolExecutor(name: string): ToolExecutor | undefined {
  return RUNTIME_EXECUTORS.get(name)
}

const EXECUTORS: Record<string, ToolExecutor> = {
  read_saved_strategy: executeReadSavedStrategy,
  create_saved_strategy: executeCreateSavedStrategy,
  edit_saved_strategy: executeEditSavedStrategy,
  run_saved_strategy: executeRunSavedStrategy,
  graph_read: executePublicGraph,
  graph_call: executePublicGraph,
  analyze_backtest: executeAnalyzeBacktest,
  set_language: executeSetLanguage,
  add_panel: executeAddPanel,
  request_panel_selection: executeRequestPanelSelection,
  delete_panel: executeDeletePanel,
  set_ticker: executeSetTicker,
  set_news_sources: executeSetNewsSources,
  set_focus_date: executeSetFocusDate,
  set_link_mode: executeSetLinkMode,
  navigate_to: executeNavigateTo,
  set_filters: executeSetFilters,
  set_history_filters: executeSetHistoryFilters,
  clear_filters: executeClearFilters,
  add_columns: executeAddColumns,
  remove_columns: executeRemoveColumns,
  set_columns: executeSetColumns,
  generate_history_layout: executeGenerateHistoryLayout,
  add_node: executeAddNode,
  connect_nodes: executeConnectNodes,
  set_node_parameters: executeSetNodeParameters,
  remove_node: executeRemoveNode,
  create_strategy_template: executeCreateStrategyTemplate,
  create_strategy_from_recipe: executeCreateStrategyFromRecipe,
  apply_graph_ops: executeApplyGraphOps,
  rerun_backtest: executeRerunBacktest,
  read_panel_state: executeReadPanelState,
  read_current_graph: executeReadCurrentGraph,
  read_backtest_results: executeReadBacktestResults,
  get_node_specs: executeGetNodeSpecs,
}

const BACKEND_TOOLS = new Set([
  'search_knowledge',
  'ask_analyst',
  'summarize_history',
  'get_platform_guide',
])

/**
 * Execute a batch of tool calls. This is the security boundary —
 * all validation and execution happens here in the frontend app,
 * not in the @edge/edge-ai package.
 */
export async function executeToolBatch(
  toolCalls: ToolCall[],
  callbacks: ActionCallbacks,
  onAnalystDelta?: (text: string) => void,
  access: TurnAccess = 'change',
  automation?: AutomationTurn,
  currentCallbacks?: () => ActionCallbacks | null,
): Promise<{ snapshot: ActionSnapshot; results: ToolResult[] }> {
  // Graph edits own their undo cards; never restore a graph across lifecycle calls.
  const snapshot = takeSnapshot(
    toolCalls.filter(
      (call) => !GRAPH_TOOL_NAMES.has(call.name) && !SAVED_STRATEGY_TOOL_NAMES.has(call.name),
    ),
    callbacks,
  )
  const results: ToolResult[] = []
  let hasError = false
  let researchFailed = false
  // The batch's analyst answers are relayed as one stream, which a voice turn
  // speaks as it comes: a later answer starts on its own paragraph, as the
  // joined answer has it (terminalPassthrough), so its first sentence does not
  // run into the last one's (2026-10-22).
  let relayedAnalyst = false
  const analystRelay = (): ((text: string) => void) | undefined => {
    if (!onAnalystDelta) return undefined
    let started = false
    return (text) => {
      if (!started && relayedAnalyst) onAnalystDelta('\n\n')
      started = true
      relayedAnalyst = true
      onAnalystDelta(text)
    }
  }

  for (const call of toolCalls) {
    if (automation?.signal.aborted || automation?.state === 'cancelled') {
      results.push(fail(call.tool_use_id, 'Task stopped; no further tool was dispatched.'))
      hasError = true
      continue
    }
    if (researchFailed && !READ_ONLY_TOOLS.has(call.name)) {
      results.push(
        fail(call.tool_use_id, 'Research failed; dependent actions were not dispatched.'),
      )
      hasError = true
      continue
    }
    if (GRAPH_TOOL_NAMES.has(call.name) || SAVED_STRATEGY_TOOL_NAMES.has(call.name)) {
      try {
        const account = useAccountStore.getState()
        assertGraphToolAccess(READ_ONLY_TOOLS.has(call.name), {
          access,
          authenticated: !!account.session?.user?.sub,
        })
        if (automation?.signal.aborted) throw new Error('Task stopped.')
      } catch (error) {
        results.push(
          fail(call.tool_use_id, error instanceof Error ? error.message : 'Graph access denied.'),
        )
        hasError = true
        continue
      }
    }
    if (access === 'read' && !READ_ONLY_TOOLS.has(call.name)) {
      results.push(
        fail(call.tool_use_id, `${call.name} is unavailable this turn (${NOT_A_CHANGE_REQUEST})`),
      )
      hasError = true
      continue
    }

    if (call.name === 'get_tools') {
      const result = executeGetTools(call, access)
      results.push(result)
      if (!result.success) hasError = true
      continue
    }

    if (BACKEND_TOOLS.has(call.name)) {
      try {
        const result =
          call.name === 'ask_analyst'
            ? await executeAskAnalyst(call, analystRelay(), automation?.signal)
            : call.name === 'summarize_history'
              ? await executeSummarizeHistory(call, callbacks, analystRelay(), automation?.signal)
              : call.name === 'get_platform_guide'
                ? await executeGetPlatformGuide(call)
                : await executeSearchKnowledge(call)
        results.push(result)
        if (!result.success) hasError = true
        if (call.name === 'ask_analyst' && !result.success) researchFailed = true
      } catch (err) {
        if (call.name === 'ask_analyst') researchFailed = true
        results.push(
          fail(
            call.tool_use_id,
            `Backend tool error: ${err instanceof Error ? err.message : String(err)}`,
          ),
        )
        hasError = true
      }
      continue
    }

    const executor =
      (Object.hasOwn(EXECUTORS, call.name) ? EXECUTORS[call.name] : undefined) ??
      RUNTIME_EXECUTORS.get(call.name)
    if (!executor) {
      results.push(fail(call.tool_use_id, `Unknown tool: ${call.name}`))
      hasError = true
      continue
    }

    try {
      const action = () => {
        const current = currentCallbacks ? currentCallbacks() : callbacks
        if (!current) throw new Error('Page context changed; no active callbacks.')
        // A key the call adds to a panel the snapshot covers comes off on Undo.
        const liveCallbacks = recordingWrites(current, snapshot)
        return automation && GRAPH_TOOL_NAMES.has(call.name) && !READ_ONLY_TOOLS.has(call.name)
          ? withGraphApiInvocation(automation, () => executor(call, liveCallbacks, automation))
          : executor(call, liveCallbacks, automation)
      }
      const result =
        automation && GRAPH_TOOL_NAMES.has(call.name) && !READ_ONLY_TOOLS.has(call.name)
          ? await withGraphAction(automation, action)
          : await action()
      results.push(result)
      if (!result.success) hasError = true
    } catch (err) {
      results.push(
        fail(
          call.tool_use_id,
          `Execution error: ${err instanceof Error ? err.message : String(err)}`,
        ),
      )
      hasError = true
    }
  }

  // Failure handling (UX-5, "Undo what landed"):
  //  - If something failed and NO mutation succeeded, auto-revert — pure
  //    safety net for an executor that half-applied before failing; there is
  //    nothing worth keeping.
  //  - If something failed but a mutation DID succeed, KEEP the applied
  //    changes: the hook shows the amber "Some steps failed — review or undo"
  //    banner whose Undo restores this snapshot. (Previously ANY failure —
  //    even a read-only ask_analyst 503 — silently rolled back successful
  //    mutations, and the banner's Undo would then re-apply a stale snapshot.)
  const anyMutationApplied = toolCalls.some(
    (tc, i) => !READ_ONLY_TOOLS.has(tc.name) && results[i]?.success,
  )
  if (hasError && !anyMutationApplied) {
    revertSnapshot(snapshot, callbacks)
  }

  return { snapshot, results }
}
